/**
 * O agendamento em linguagem natural (AB#87330), para qualquer cron válido —
 * nunca o cron cru. Os formatos que as pessoas usam ganham frase de gente
 * ("A cada 10 min, das 06:00 às 08:50, de segunda a sexta"); o resto é descrito
 * campo a campo.
 *
 * Os textos ficam aqui, e não no i18n de cada app, porque a api também descreve
 * (MCP) e não tem i18next. A saída multi-idioma segue o formato `{ pt, en }` dos
 * campos traduzidos do banco.
 */
import { parseCron } from './cron.js';
export const SCHEDULE_LOCALES = ['pt', 'en'];
const WORDS = {
    pt: {
        everyMinute: 'a cada minuto',
        everyNMinutes: (n) => `a cada ${n} min`,
        everyHour: 'a cada hora',
        everyNHours: (n) => `a cada ${n} h`,
        atMinute: (m) => `no minuto ${m}`,
        window: (from, to) => `das ${from} às ${to}`,
        everyDay: 'todo dia',
        at: (times) => `às ${times}`,
        and: 'e',
        or: 'ou',
        dayNames: ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'],
        everyWeekday: (day) => `${day === 0 || day === 6 ? 'todo' : 'toda'} ${dayName(day, WORDS.pt)}`,
        weekdayRange: (from, to) => `de ${from} a ${to}`,
        monthDay: (day) => `todo dia ${day}`,
        monthDays: (days) => `nos dias ${days}`,
        monthDayRange: (from, to) => `do dia ${from} ao dia ${to}`,
        lastDay: 'no último dia do mês',
        monthNames: [
            'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
            'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
        ],
        inMonths: (months) => `em ${months}`,
        monthRange: (from, to) => `de ${from} a ${to}`,
        minutesList: (list) => `nos minutos ${list}`,
        minutesStep: (n, from) => `a cada ${n} min a partir do minuto ${from}`,
        hoursRange: (from, to) => `das ${from} às ${to}`,
        hoursList: (list) => `nas horas ${list}`,
        invalid: 'Agendamento inválido',
    },
    en: {
        everyMinute: 'every minute',
        everyNMinutes: (n) => `every ${n} min`,
        everyHour: 'every hour',
        everyNHours: (n) => `every ${n} h`,
        atMinute: (m) => `at minute ${m}`,
        window: (from, to) => `from ${from} to ${to}`,
        everyDay: 'every day',
        at: (times) => `at ${times}`,
        and: 'and',
        or: 'or',
        dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        everyWeekday: (day) => `every ${dayName(day, WORDS.en)}`,
        weekdayRange: (from, to) => `${from} to ${to}`,
        monthDay: (day) => `on day ${day} of the month`,
        monthDays: (days) => `on days ${days}`,
        monthDayRange: (from, to) => `from day ${from} to ${to}`,
        lastDay: 'on the last day of the month',
        monthNames: [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December',
        ],
        inMonths: (months) => `in ${months}`,
        monthRange: (from, to) => `${from} to ${to}`,
        minutesList: (list) => `at minutes ${list}`,
        minutesStep: (n, from) => `every ${n} min starting at minute ${from}`,
        hoursRange: (from, to) => `from ${from} to ${to}`,
        hoursList: (list) => `during hours ${list}`,
        invalid: 'Invalid schedule',
    },
};
/** Até quantos horários a frase lista um a um ("às 08:00, 12:00 e 18:00"). */
const MAX_LISTED_TIMES = 6;
export function describeCron(cron, options) {
    const words = WORDS[options.locale];
    const parsed = parseCron(cron);
    const sentence = parsed ? describeParsed(parsed, words) : words.invalid;
    return options.timezone ? `${sentence} (${options.timezone})` : sentence;
}
/** A descrição nos dois idiomas, no formato `{ pt, en }` dos campos traduzidos. */
export function describeCronI18n(cron, timezone) {
    return {
        pt: describeCron(cron, { locale: 'pt', timezone }),
        en: describeCron(cron, { locale: 'en', timezone }),
    };
}
function describeParsed(cron, w) {
    const days = dayPhrase(cron, w);
    const months = cron.month.any ? null : w.inMonths(monthList(cron.month.values, w));
    const tail = (parts) => parts.filter(Boolean).join(', ');
    const interval = intervalPhrase(cron, w);
    if (interval)
        return capitalize(tail([interval.head, interval.window, days, months]));
    const times = timesOf(cron);
    if (times) {
        const lead = days ?? w.everyDay;
        return capitalize(tail([`${lead} ${w.at(list(times, w))}`, months]));
    }
    return capitalize(tail([days ?? w.everyDay, minutesPhrase(cron.minute, w), hoursPhrase(cron.hour, w), months]));
}
/** "A cada N min/h", quando os minutos ou as horas formam um intervalo regular. */
function intervalPhrase(cron, w) {
    const minute = cron.minute;
    const hour = cron.hour;
    const minuteStep = minute.any ? 1 : stepFromZero(minute.values, 59);
    if (minuteStep !== null) {
        const head = minuteStep === 1 ? w.everyMinute : w.everyNMinutes(minuteStep);
        if (hour.any)
            return { head, window: null };
        if (isContiguous(hour.values)) {
            const lastMinute = last(minute.values);
            return { head, window: w.window(hhmm(first(hour.values), 0), hhmm(last(hour.values), lastMinute)) };
        }
        return { head, window: w.hoursList(list(hour.values.map(String), w)) };
    }
    // Horas em intervalo só quando o campo foi escrito como intervalo (`*`, `*/2`,
    // `6-18`, `6-18/2`): `8,12,18` lê melhor como lista de horários.
    if (minute.values.length !== 1 || hour.values.length < 2 || hour.token.includes(','))
        return null;
    const step = uniformStep(hour.values);
    if (step === null)
        return null;
    const m = first(minute.values);
    const head = [step === 1 ? w.everyHour : w.everyNHours(step), m === 0 ? null : w.atMinute(m)]
        .filter(Boolean)
        .join(' ');
    const from = first(hour.values);
    const to = last(hour.values);
    const fullDay = from === 0 && to + step > 23;
    return { head, window: fullDay ? null : w.window(hhmm(from, m), hhmm(to, m)) };
}
function timesOf(cron) {
    const total = cron.minute.values.length * cron.hour.values.length;
    if (total === 0 || total > MAX_LISTED_TIMES)
        return null;
    const times = [];
    for (const h of cron.hour.values)
        for (const m of cron.minute.values)
            times.push(hhmm(h, m));
    return times;
}
function dayPhrase(cron, w) {
    const day = cron.day;
    const weekday = cron.weekday;
    const anyWeekday = weekday.any || weekday.values.length === 7;
    const monthPart = day.lastDay ? w.lastDay : day.any ? null : monthDayPhrase(day.values, w);
    const weekPart = anyWeekday ? null : weekdayPhrase(weekday.values, w);
    if (monthPart && weekPart)
        return `${monthPart} ${w.or} ${weekPart}`;
    return monthPart ?? weekPart;
}
function weekdayPhrase(days, w) {
    if (days.length === 1)
        return w.everyWeekday(first(days));
    if (days.length >= 3 && isContiguous(days)) {
        return w.weekdayRange(dayName(first(days), w), dayName(last(days), w));
    }
    return list(days.map((d) => dayName(d, w)), w);
}
function monthDayPhrase(days, w) {
    if (days.length === 1)
        return w.monthDay(first(days));
    if (days.length >= 3 && isContiguous(days))
        return w.monthDayRange(first(days), last(days));
    return w.monthDays(list(days.map(String), w));
}
function monthList(months, w) {
    if (months.length >= 3 && isContiguous(months)) {
        return w.monthRange(monthName(first(months), w), monthName(last(months), w));
    }
    return list(months.map((m) => monthName(m, w)), w);
}
function minutesPhrase(field, w) {
    if (field.values.length === 1)
        return w.atMinute(first(field.values));
    const step = uniformStep(field.values);
    if (step !== null && field.values.length >= 3)
        return w.minutesStep(step, first(field.values));
    return w.minutesList(list(field.values.map(String), w));
}
function hoursPhrase(field, w) {
    if (field.any)
        return null;
    if (field.values.length >= 2 && isContiguous(field.values)) {
        return w.hoursRange(hhmm(first(field.values), 0), hhmm(last(field.values), 59));
    }
    return w.hoursList(list(field.values.map(String), w));
}
/** O passo de uma sequência que começa em 0 e vai até onde ele alcança dentro de `max`. */
function stepFromZero(values, max) {
    if (first(values) !== 0)
        return null;
    const step = uniformStep(values);
    if (step === null)
        return null;
    return last(values) + step > max ? step : null;
}
function uniformStep(values) {
    if (values.length < 2)
        return null;
    const step = (values[1] ?? 0) - first(values);
    for (let i = 2; i < values.length; i++)
        if ((values[i] ?? 0) - (values[i - 1] ?? 0) !== step)
            return null;
    return step;
}
function isContiguous(values) {
    return uniformStep(values) === 1;
}
function list(items, w) {
    if (items.length <= 1)
        return items.join('');
    return `${items.slice(0, -1).join(', ')} ${w.and} ${items[items.length - 1] ?? ''}`;
}
function first(values) {
    return values[0] ?? 0;
}
function last(values) {
    return values[values.length - 1] ?? 0;
}
function dayName(day, w) {
    return w.dayNames[day] ?? String(day);
}
function monthName(month, w) {
    return w.monthNames[month - 1] ?? String(month);
}
function hhmm(hour, minute) {
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}
function capitalize(text) {
    return text.charAt(0).toUpperCase() + text.slice(1);
}
//# sourceMappingURL=describe.js.map